import { apiService } from '../utils/apiService';
import { API_CONFIG } from '../utils/constants';

const DOCS = API_CONFIG.DOCUMENTS;

const ensureArray = (value) => (Array.isArray(value) ? value : []);

const buildQuery = (params = {}) => {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;

    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (entry !== undefined && entry !== null && entry !== '') {
          search.append(key, String(entry));
        }
      });
      return;
    }

    search.append(key, String(value));
  });

  const query = search.toString();
  return query ? `?${query}` : '';
};

const documentService = {
  
  getAvailableTemplates: async (companyId) => {
    const query = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
    const response = ensureArray(await apiService.get(`${DOCS.AVAILABLE_TEMPLATES}${query}`));

    return response.map((template) => ({
      ...template,
      hasRating: template.hasRating ?? false,
      statuses: template.statuses ?? [],
    }));
  },

  getDocuments: async (params) => apiService.get(`${DOCS.BASE}${buildQuery(params)}`),

  getInspectionDocuments: async (params) => {
    const response = await apiService.get(`${DOCS.INSPECTIONS}${buildQuery(params)}`);

    return {
      items: ensureArray(response?.items),
      totalCount: response?.totalCount ?? 0,
      currentPage: response?.currentPage ?? 0,
      statusCounts: {
        inProgress: response?.statusCounts?.inProgress ?? 0,
        completed: response?.statusCounts?.completed ?? 0,
      },
    };
  },

  getTemplateCompanies: async (templateId) =>
    ensureArray(await apiService.get(`${DOCS.TEMPLATE_COMPANIES}/${templateId}/companies`)),

  getTemplateFields: async (templateId) =>
    ensureArray(await apiService.get(`${DOCS.TEMPLATE_FIELDS}/${templateId}/fields`)),

  getDocument: async (documentId) => apiService.get(`${DOCS.BASE}/${documentId}`),

  getDocumentImages: async (documentId) =>
    ensureArray(await apiService.get(`${DOCS.BASE}/${documentId}/images`)),

  getDocumentPdf: async (documentId) => {
    const result = await apiService.get(`${DOCS.BASE}/${documentId}/pdf`);
    return result?.url;
  },

  /**
   * Word-template documents. The API fills the template and converts it on the
   * spot, so it sends the PDF itself rather than a link; it is turned into a local
   * URL that opens the same way. Converting takes a few seconds.
   */
  getDocumentWordPdf: async (documentId) => {
    const blob = await apiService.get(`${DOCS.WORD}/${documentId}/pdf`, {
      responseType: 'blob',
      headers: { Accept: 'application/pdf' },
      timeout: 120000,
    });

    return URL.createObjectURL(blob);
  },

  getInspectionEventPreview: async (documentId) =>
    apiService.get(`${DOCS.BASE}/${documentId}/event-preview`),

  convertInspectionToEvent: async (documentId, payload) =>
    apiService.post(`${DOCS.BASE}/${documentId}/convert-to-event`, payload, { timeout: 120000 }),

  deleteDocumentImage: async (documentId, imageId) =>
    apiService.delete(`${DOCS.BASE}/${documentId}/images/${imageId}`),

  createDocument: async (payload) => apiService.post(DOCS.BASE, payload, { timeout: 120000 }),

  updateDocument: async (documentId, payload) =>
    apiService.put(`${DOCS.BASE}/${documentId}`, payload, { timeout: 120000 }),

  getCurrencies: async () => ensureArray(await apiService.get(DOCS.CURRENCIES)),

  getCompanyFleet: async (companyId, { pageSize = 20, pageIndex = 0, searchTerm } = {}) => {
    const empty = { items: [], totalCount: 0, currentPage: pageIndex, hasNextPage: false, nextPage: pageIndex };

    const query = buildQuery({ pageSize, pageIndex, searchTerm: searchTerm?.trim() || undefined });

    try {
      const result = await apiService.get(`${DOCS.COMPANY_FLEETS}/${companyId}${query}`);

      return {
        ...empty,
        ...(result ?? {}),
        items: ensureArray(result?.items),
      };
    } catch (error) {
      // The API 404s rather than returning an empty page, so every search that
      // matches nothing arrives here.
      if (error?.response?.status === 404) return empty;
      throw error;
    }
  },

  getEventDocumentFiles: async (breakdownId) => {
    if (!breakdownId) return [];
    const result = await apiService.get(
      `${API_CONFIG.EVENTS.GET_MEDIA_URLS}/${breakdownId}?location=DocumentLibrary`
    );

    return ensureArray(result).map((file) => ({
      id: file.id,
      url: file.sasUrl,
      name: file.originalName || 'Document.pdf',
      mediaType: file.mediaType,
      createdOn: file.createdOn,
    }));
  },
};

export default documentService;
